import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useUser } from "../../context/UserContext";
import styles from "./LinkButton.module.scss";
import { useLinkedAccounts } from "../../context/LinkedAccountsContext";
import { linkProfileMethods } from "./functions"

import PSN from "@/assets/platforms/playstation.svg?react";
import Epicgames from "@/assets/platforms/epicgames.svg?react";
import Nintendo from "@/assets/platforms/nintendo.svg?react";
import Steam from "@/assets/platforms/steam.svg?react";
import Xbox from "@/assets/platforms/xbox.svg?react";
import Discord from "@/assets/platforms/discord.svg?react";
import { AlertTriangle } from "lucide-react";
import { Platform } from "../../types";

interface linkButtonProps {
    platform: Platform
}

const platformLabels: Record<Platform, string> = {
    steam: "Steam",
    playstation: "PlayStation",
    xbox: "Xbox",
    nintendo: "Nintendo",
    epicgames: "Epic Games",
    discord: "Discord",
};

export const LinkButton = ({ platform }: linkButtonProps) => {
    const { user, loading: userLoading } = useUser();
    const {
        accounts,
        loading: accountsLoading,
        refreshLinkedAccounts,
        discordMembership,
        membershipLoading,
        membershipError,
        refreshMembership,
        rejoinDiscord,
    } = useLinkedAccounts();
    const [linked, setLinked] = useState(false);
    const [disabled, setDisabled] = useState(true);
    const [showConfirm, setShowConfirm] = useState(false);
    const [unlinking, setUnlinking] = useState(false);
    const [rejoining, setRejoining] = useState(false);

    const iconMap: Record<Platform, any> = {
        steam: <Steam />,
        playstation: <PSN />,
        xbox: <Xbox />,
        nintendo: <Nintendo />,
        epicgames: <Epicgames/>,
        discord: <Discord/>
    };

    useEffect(() => {
        if (userLoading || accountsLoading) {
            setDisabled(true);
            return;
        }

        setDisabled(false);
        if (accounts) {
           setLinked(!!accounts[platform])
        }
    }, [userLoading, accountsLoading, accounts]);

    const link = async () => {
        linkProfileMethods[platform].link(user)
    };

    const handleUnlinkClick = () => {
        setShowConfirm(true);
    };

    const confirmUnlink = async () => {
        setUnlinking(true);
        try {
            await linkProfileMethods[platform].unlink(user);
            await refreshLinkedAccounts();
            // Membership is independent of linking; refresh it so the rejoin
            // UI disappears once the Discord link is removed.
            if (platform === "discord") {
                await refreshMembership();
            }
        } catch (err) {
            console.error("Failed to unlink:", err);
        } finally {
            setUnlinking(false);
            setShowConfirm(false);
        }
    };

    const handleRejoin = async () => {
        if (rejoining) return;
        setRejoining(true);
        try {
            const res = await rejoinDiscord();
            if (res.reauthRequired) {
                // Stored authorization is no longer usable — re-run OAuth to
                // obtain fresh scopes, matching the existing discord link flow.
                linkProfileMethods.discord.link(user);
            }
        } catch (err) {
            console.error("Failed to rejoin Discord:", err);
        } finally {
            setRejoining(false);
        }
    };

    // Discord is linked, membership resolved without error, and the user is
    // confirmed to NOT be in the server — show the warning + rejoin affordance.
    const discordNotMember =
        platform === "discord" &&
        linked &&
        discordMembership !== null &&
        discordMembership.linked &&
        !discordMembership.isMember &&
        !membershipError;

    return (
        <div className={styles.badgeWrapper}>
            <button
                className={[
                    styles.platformbadge,
                    styles[platform],
                    linked && styles.linked,
                    discordNotMember && styles.notMember,
                ].filter(Boolean).join(" ")}
                onClick={!linked ? link : handleUnlinkClick}
                disabled={disabled}
                title={
                    userLoading || accountsLoading
                        ? "Loading..."
                        : linked
                            ? `Unlink ${platformLabels[platform]}`
                            : `Link ${platformLabels[platform]}`
                }
            >
                {iconMap[platform]}
            </button>

            {discordNotMember && (
                <span
                    role="button"
                    tabIndex={0}
                    className={styles.memberWarning}
                    onClick={rejoining ? undefined : handleRejoin}
                    onKeyDown={(e) => {
                        if (rejoining) return;
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleRejoin();
                        }
                    }}
                    aria-disabled={rejoining}
                    data-tooltip="Not a member of the Discord server — click to join"
                    aria-label="Not a member of the Discord server — click to join"
                >
                    <AlertTriangle
                        className={styles.memberWarningIcon}
                        strokeWidth={2.5}
                        aria-hidden="true"
                    />
                </span>
            )}

            {showConfirm && ReactDOM.createPortal(
                <div className={styles.modalOverlay} onClick={() => setShowConfirm(false)}>
                    <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                        <h3>Unlink {platformLabels[platform]}</h3>
                        <p>
                            Are you sure you want to unlink your {platformLabels[platform]} account?
                            This will remove all games and achievements associated with this platform from your profile.
                        </p>
                        <div className={styles.modalActions}>
                            <button
                                className={styles.cancelBtn}
                                onClick={() => setShowConfirm(false)}
                                disabled={unlinking}
                            >
                                Cancel
                            </button>
                            <button
                                className={styles.unlinkBtn}
                                onClick={confirmUnlink}
                                disabled={unlinking}
                            >
                                {unlinking ? "Unlinking..." : "Unlink"}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};
